import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { chatAPI, feedbackAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { ArrowUp, User, Trash2, Smartphone, Monitor, ThumbsUp, ThumbsDown, Bell } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { sanitizeHTML, isHTML, stripHTML } from '../utils/htmlContentRenderer';
import { formatMarkdownText } from '../utils/markdownFormatter';
import { handleApiError } from '../utils/errorHandler';
import textConfig from '../config/textConfig';
import ChatMessageSkeleton from '../components/ChatMessageSkeleton';
import FileUploadButton from '../components/FileUploadButton';
import FilePreview from '../components/FilePreview';
import { useFileUpload } from '../hooks/useFileUpload';
import { useUploadTrigger } from '../hooks/useUploadTrigger';
import { initIframeKeyboardFix } from '../utils/iframeKeyboardFix';
import '../styles/Chat.css';
import logger from '../utils/logger';
import { attachmentDisplayName, attachmentDisplayNames } from '../utils/attachmentDisplay';
import { getCardTypewriterText, parseRoomSelectionHeader } from '../components/chat/cardTypeHelpers';
import MessageCardRenderer from '../components/chat/MessageCardRenderer';
import ImageLightbox from '../components/chat/ImageLightbox';
import ConnectCalendarButton from '../components/chat/connect-calendar/ConnectCalendarButton';
import { isGoogleCalendarPageLink, findGoogleCalendarPageLink, stripPointerBeforeLink } from '../utils/googleCalendarLink';
import { isOrganizationEventMessage, getEventData, getEventHeader } from '../components/chat/organization-events/eventParser';
import { isCalendarEventMessage, getCalendarEventData } from '../components/chat/calendar-events/calendarEventParser';
import { isBookingListMessage, getBookingListData } from '../components/chat/booking-list/bookingListParser';
import { isCancelBookingMessage, getCancelBookingData } from '../components/chat/cancel-booking/cancelBookingParser';
import WebChips from '../components/chat/web-chips/WebChips';
import { CHAT_FUNCTIONS } from '../config/chatFunctionNames';
import { parseUpdateBookingConfirmation } from '../utils/updateBookingConfirmation';
import { isTicketFlowEnded } from '../utils/ticketFlowSignals';
import TicketCard from '../components/chat/TicketCard';
import { getTicketCard, findTicketContext } from '../components/chat/ticketCardModel';
import ProfileCompletionModal from '../components/ProfileCompletionModal';
import {
  getOnboardingGate,
  verificationDeferred,
  verificationRequiredNow,
  GATE_NOTICE,
} from '../utils/onboardingGate';

// History pagination: newest window fetched on mount, then older pages are
// pulled in as the user scrolls to the top (same UX as Live Conversations).
const INITIAL_HISTORY_LIMIT = 50;
const OLDER_HISTORY_PAGE_SIZE = 20;

// Starter quick-reply chips shown to brand-new users (no messages yet) via
// the regular WebChips row. Replaced by backend-driven chips after the
// first assistant response.
const STARTER_CHIPS = [
  { value: 'Book a Room Now', label: 'Book a Room Now' },
  { value: 'Upcoming Events', label: 'Upcoming Events' },
  { value: 'Support Ticket', label: 'Support Ticket' },
];

// Helper function to get proper image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  
  // Check if it's already a complete URL (starts with http:// or https://)
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // Otherwise, it's a relative path, so prepend the origin
  return `${window.location.origin}${imagePath}`;
};

// Is this a create_booking proposal/confirmation? Live messages carry
// function_name; history rows loaded from the DB often have it null but keep
// raw_output (same split the community-announcement parser documents).
const isCreateBookingMessage = (message) => {
  if (!message) return false;
  const raw = message.raw_output || {};
  return (
    message.function_name === CHAT_FUNCTIONS.CREATE_BOOKING ||
    raw.type === 'book_room' ||
    raw.awaiting_confirmation === true ||
    !!(raw.booking_reference || raw.booking_id)
  );
};

// Parse booking confirmation message into structured data.
//
// `allowMissingImage` is set for create_booking messages. The backend only
// emits the `Image:` line when the room has a photo, so gating the card on it
// meant photo-less rooms lost the entire card and fell back to raw text. The
// renderer already treats the image as optional. Other flows keep the strict
// gate so they can't be captured by accident.
const parseBookingCard = (content, { allowMissingImage = false } = {}) => {
  if (!content) return null;

  // Check if this looks like a booking confirmation (has Room:, plus Image:
  // unless the caller vouched for the message being a create_booking one)
  if (!content.includes('Room:')) return null;
  if (!allowMissingImage && !content.includes('Image:')) return null;

  const lines = content.split('\n');
  const data = {};

  for (const line of lines) {
    // Basic booking details
    if (line.startsWith('Room:')) data.room = line.replace('Room:', '').trim();
    else if (line.startsWith('Location:')) data.location = line.replace('Location:', '').trim();
    else if (line.startsWith('Date:')) data.date = line.replace('Date:', '').trim();
    // Handle "Time: 03:00 PM - 04:00 PM" format from confirmation messages
    else if (line.startsWith('Time:')) {
      const timeRange = line.replace('Time:', '').trim();
      // Parse "03:00 PM - 04:00 PM" format
      if (timeRange.includes(' - ')) {
        const [start, end] = timeRange.split(' - ').map(t => t.trim());
        data.start = start;
        data.end = end;
      } else {
        data.timeRange = timeRange; // Fallback for other formats
      }
    }
    else if (line.startsWith('Start:') || line.startsWith('Start Time:')) {
      data.start = line.replace('Start:', '').replace('Start Time:', '').trim();
    }
    else if (line.startsWith('End:') || line.startsWith('End Time:')) {
      data.end = line.replace('End:', '').replace('End Time:', '').trim();
    }
    else if (line.startsWith('Duration:')) data.duration = line.replace('Duration:', '').trim();
    else if (line.startsWith('Capacity:')) data.capacity = line.replace('Capacity:', '').trim();
    else if (line.startsWith('Rate:')) data.rate = line.replace('Rate:', '').trim();
    else if (line.startsWith('Amenities:')) {
      data.amenities = line.replace('Amenities:', '').trim().split(',').map(a => a.trim()).filter(Boolean);
    }
    else if (line.startsWith('Image:')) data.image = line.replace('Image:', '').trim();

    // Booking reference/ID
    else if (line.startsWith('Booking ID:')) data.bookingId = line.replace('Booking ID:', '').trim();
    else if (line.startsWith('Reference:')) data.reference = line.replace('Reference:', '').trim();
    else if (line.startsWith('Confirmation:')) data.confirmation = line.replace('Confirmation:', '').trim();

    // Billing details
    else if (line.startsWith('Credits Used:')) data.creditsUsed = line.replace('Credits Used:', '').trim();
    else if (line.startsWith('Coins Used:')) data.coinsUsed = line.replace('Coins Used:', '').trim();
    else if (line.startsWith('Fee:')) data.fee = line.replace('Fee:', '').trim();
    else if (line.startsWith('Price:')) data.price = line.replace('Price:', '').trim();
    else if (line.startsWith('Discount Amount:')) data.discountAmount = line.replace('Discount Amount:', '').trim();
    else if (line.startsWith('Calculated Discount:')) data.calculatedDiscount = line.replace('Calculated Discount:', '').trim();
    else if (line.startsWith('Discounted Price:')) data.discountedPrice = line.replace('Discounted Price:', '').trim();
    else if (line.startsWith('Payment Status:')) data.paymentStatus = line.replace('Payment Status:', '').trim();
  }

  // Must have at least a room (and an image, unless it's a create_booking message)
  if (!data.room) return null;
  if (!allowMissingImage && !data.image) return null;

  // Extract action text (everything after Image: line, but before billing details section)
  const imageIndex = lines.findIndex(line => line.startsWith('Image:'));
  const billingIndex = lines.findIndex(line => line.includes('--- Billing Details ---'));

  if (imageIndex >= 0) {
    const endIndex = billingIndex > imageIndex ? billingIndex : lines.length;
    data.actionText = lines.slice(imageIndex + 1, endIndex)
      .filter(line => line.trim() && !line.includes('--- Billing Details ---'))
      .join('\n');
  }

  // Determine if this is a proposal (waiting for confirmation) or actual confirmation
  // Proposal messages contain "Reply 'Yes'" or "Reply \"Yes\"" in action text
  // Confirmed bookings have billing details OR booking reference/ID AND do not ask for confirmation
  // No `Image:` line means no anchor for actionText, so fall back to the whole
  // message when deciding proposal-vs-confirmed. Unchanged when one exists.
  const proposalSource = imageIndex >= 0 ? data.actionText : content;
  const hasProposalText = !!proposalSource && (
    proposalSource.includes("Reply 'Yes'") ||
    proposalSource.includes('Reply "Yes"') ||
    proposalSource.includes("Reply 'yes'") ||
    proposalSource.includes('Reply "yes"') ||
    proposalSource.includes("Say 'Yes'") ||
    proposalSource.includes('Say "Yes"')
  );
  const hasConfirmationData = !!(data.creditsUsed || data.coinsUsed || data.price || data.bookingId || data.reference || data.confirmation);

  // Only show as proposal if it asks for confirmation AND has no billing data
  data.isProposal = hasProposalText && !hasConfirmationData;
  // Only show as confirmed if it has billing data AND does NOT ask for confirmation
  data.isConfirmed = hasConfirmationData && !hasProposalText;

  return data;
};

// Helper function to check if a ticket was cancelled
// Checks both backend entities flag AND specific cancellation messages
const isTicketCancelled = (message, messageContent) => {
  if (!message || !messageContent) return false;

  return (message.entities?.exit_requested === true &&
          message.entities?.exit_type === 'cancel') ||
         messageContent.includes('Ok, cancelled.') ||
         messageContent.includes('No problem, ticket cancelled.') ||
         messageContent.includes('Got it. I won\'t submit the ticket.') ||
         messageContent.includes('I won\'t create the ticket');
};


const Chat = () => {
  const [messages, setMessages] = useState([]);
  // Latest user-message id — used as a collapse signal for expandable cards
  // (e.g. organization event "View details" auto-collapses on new question)
  const latestUserMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user') return messages[i].id;
    }
    return null;
  }, [messages]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [typingMessageId, setTypingMessageId] = useState(null);
  // Quick-reply chips supplied by the latest backend response (response.web_chips).
  // Cleared the moment the user sends anything; re-populated when a new response arrives.
  const [webChips, setWebChips] = useState([]);
  const [displayedText, setDisplayedText] = useState({});
  const [showSettings, setShowSettings] = useState(false);
  const [clearingHistory, setClearingHistory] = useState(false);
  const [messageFeedback, setMessageFeedback] = useState({}); // Track feedback per message: { messageId: { rating, showTextBox, feedbackText, submitting } }
  const [isCreatingTicket, setIsCreatingTicket] = useState(false); // Track if user is in support ticket creation flow
  const [, setAttachmentsSent] = useState(false); // Track if attachments already sent (prevent duplicates)
  // In-app preview for user-submitted image URLs (assistant images still use existing flows).
  const [lightbox, setLightbox] = useState({ open: false, images: [], index: 0 });
  const closeLightbox = useCallback(() => setLightbox(prev => ({ ...prev, open: false })), []);
  const openLightbox = useCallback((urls, startIndex) => {
    setLightbox({ open: true, images: urls, index: startIndex });
  }, []);
  // Infinite scroll-up history: spinner state + ref-based guards (refs so the
  // document-level scroll listener, mounted once, never sees stale values).
  const [loadingOlder, setLoadingOlder] = useState(false);
  const hasMoreHistoryRef = useRef(false);
  const loadingOlderRef = useRef(false);
  const historyOffsetRef = useRef(0);
  // Set just before prepending an older page; consumed by a layout effect to
  // restore the scroll position so the viewport doesn't jump.
  const pendingScrollAdjustRef = useRef(null);
  const loadOlderMessagesRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingIntervalRef = useRef(null);
  const abortControllerRef = useRef(null);
  const textareaRef = useRef(null);
  const userScrolledUpRef = useRef(false);
  const isAutoScrollingRef = useRef(false);
  // When set, the next messages.length change anchors the newly-sent user
  // message near the top of the viewport (ChatGPT-style), instead of jumping
  // to bottom and hiding the user's question above the scroll.
  const shouldAnchorUserMessageRef = useRef(false);
  const { user, checkAuth } = useAuth();
  // Onboarding gate (A/B): once the backend flags verification_required (either
  // on a chat response or on the user's refreshed onboarding_gate), the
  // composer locks and the phone-verification modal takes over.
  const [verificationGated, setVerificationGated] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const location = useLocation();

  // Two completely independent upload buckets — one per domain. Sharing
  // a single bucket across unrelated flows (tickets vs. community
  // announcements) caused images uploaded for a CA to bleed into a later
  // ticket compose. Scoping isolates state, storage, and lifecycle.
  //
  //   ticketUpload — persisted; accumulates across the multi-message ticket
  //                  flow until the ticket is submitted or cancelled
  //   caUpload     — in-memory only; one-shot per CA submit, never written
  //                  to localStorage so it cannot survive a refresh or
  //                  leak into other flows
  const ticketUpload = useFileUpload({ scope: 'ticket', persist: true });
  const caUpload = useFileUpload({ scope: 'ca', persist: false });

  // Trigger-based upload unlock (e.g. typing "+image" opens the upload UI
  // for permitted users). Config: src/config/uploadAccessConfig.js.
  // The support-ticket flow continues to open the upload UI on its own.
  const uploadTrigger = useUploadTrigger(newMessage, user);
  const uploadMaxFiles = uploadTrigger.activeRule?.maxFiles ?? 3;

  // Once the user has uploaded an image for a CA, the UI must STAY visible
  // even if they edit out the trigger word — the upload is theirs to keep
  // or remove explicitly via the × button (or send to clear). Hiding the
  // thumbnails the moment "+image" leaves the text creates the illusion
  // that the upload was lost, and re-typing the trigger appears to bring
  // "old" images back from nowhere. Sticky-on-attachments fixes that.
  const hasCaAttachments = caUpload.attachments.length > 0;
  const isUploadUIVisible =
    isCreatingTicket || uploadTrigger.isOpen || hasCaAttachments;

  // Pick the active bucket strictly by current flow. Ticket wins when both
  // signals are true (e.g., user typed "+image" inside an active ticket).
  // The CA bucket stays active while it has uploads, so the user can
  // manage them after editing out the trigger.
  const activeUpload = isCreatingTicket
    ? ticketUpload
    : (uploadTrigger.isOpen || hasCaAttachments)
      ? caUpload
      : null;

  const attachments = activeUpload?.attachments ?? [];
  const uploading = activeUpload?.uploading ?? false;
  const uploadProgress = activeUpload?.uploadProgress ?? 0;
  const handleFileUpload = activeUpload?.handleFileUpload ?? (() => {});
  const removeAttachment = activeUpload?.removeAttachment ?? (() => {});
  const clearAttachments = activeUpload?.clearAttachments ?? (() => {});


  // Debug: Log user data to check if user_image is available
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      logger.debug('CHAT', 'Current user data:', user);
      logger.debug('CHAT', 'User image:', user?.user_image);
    }
  }, [user]);

  // Load all messages on mount. Approved users always load history (phone-
  // verification spec §4.1 — approval means chat access, even without a
  // verified phone). Pending users only load when profile is complete; until
  // then the ProfileCompletionModal covers the screen anyway.
  // Onboarding gate (A/B): in deferred-verification mode unverified users chat
  // inside their free-message window, so history must load for them too.
  useEffect(() => {
    const hasPhone = user?.phone_number && user.phone_number.length > 0;
    const hasImage = user?.user_image && user.user_image.length > 0;
    const hasSmsConsent = user?.has_sms_consent === true;
    const profileComplete = hasPhone && hasImage && hasSmsConsent;
    const isApproved = user?.status === 'approved';
    const gateDeferred = verificationDeferred(getOnboardingGate(user));

    if (profileComplete || isApproved || gateDeferred) {
      loadMessages();
    } else {
      logger.debug('CHAT', 'Skipping message load - profile incomplete and user not approved');
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run only when user?.phone_number, user?.user_image, user?.has_sms_consent, user?.status, user?.onboarding_gate?.free_message_limit change; loadMessages and user is defined in render and reads the latest state
  }, [user?.phone_number, user?.user_image, user?.has_sms_consent, user?.status, user?.onboarding_gate?.free_message_limit]);

  // Onboarding gate (A/B): keep the composer lock in sync with the user's
  // server-computed gate — covers hard refresh mid-gate (verification_required
  // arrives via /auth/profile) and unlocks after successful verification.
  useEffect(() => {
    const gate = getOnboardingGate(user);
    if (!gate) return; // legacy mode — chat responses drive the lock, if ever
    if (verificationRequiredNow(gate)) {
      setVerificationGated(true);
    } else {
      setVerificationGated(false);
    }
  }, [user]);

  // Onboarding gate (A/B): restore a message the gate blocked earlier. Once
  // the user's phone is verified and the composer is unlocked, refill the
  // input with their lost text (e.g. "book a room") so they just press send.
  // Never clobber text they've already typed.
  useEffect(() => {
    if (verificationGated) return;
    const hasVerifiedPhone = user?.phone_number && user.phone_number.length > 0;
    if (!hasVerifiedPhone) return;
    const blocked = sessionStorage.getItem('gate_blocked_message');
    if (blocked) {
      sessionStorage.removeItem('gate_blocked_message');
      setNewMessage(prev => (prev && prev.trim() ? prev : blocked));
      logger.info('CHAT', 'Onboarding gate: restored blocked message into composer');
    }
  }, [verificationGated, user?.phone_number]);


  // When a new message arrives: if the user just sent one, anchor their
  // bubble near the top of the viewport so the question stays visible while
  // the response renders below. Otherwise, don't auto-scroll — respect
  // whatever the user is currently reading.
  useEffect(() => {
    if (!shouldAnchorUserMessageRef.current) return;

    const timer = setTimeout(() => {
      anchorLastUserMessageToTop();
      shouldAnchorUserMessageRef.current = false;
    }, 100);

    return () => clearTimeout(timer);
  }, [messages.length]);

  // Anchor ONCE when the assistant response starts (typingMessageId becomes
  // non-null). After that, the user is in full control of scroll — we never
  // fight them. Layout-shift cases (image loads) are handled per-image via
  // their own `onLoad` anchors, which also bail on userScrolledUpRef.
  useEffect(() => {
    if (!typingMessageId) return;
    if (userScrolledUpRef.current) return;
    anchorLastUserMessageToTop();
  }, [typingMessageId]);

  // Detect user manual scroll: any direct user input (wheel, touch drag,
  // arrow / page keys) immediately marks userScrolledUpRef = true so the
  // streaming re-anchor stops fighting them. We attach wheel/touch/key to
  // `document` so it works regardless of when `.messages-container` mounts
  // and even when the events fire on a child element. The scroll listener
  // re-enables auto-anchor when the user returns to the bottom on their own.
  useEffect(() => {
    const isInsideMessages = (el) => !!(el && el.closest && el.closest('.messages-container'));

    const handleWheel = (e) => {
      if (isInsideMessages(e.target)) userScrolledUpRef.current = true;
    };
    const handleTouchMove = (e) => {
      if (isInsideMessages(e.target)) userScrolledUpRef.current = true;
    };
    const handleKeyDown = (e) => {
      if (['PageUp', 'PageDown', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Space'].includes(e.code)) {
        userScrolledUpRef.current = true;
      }
    };

    const handleScroll = (e) => {
      const container = e.target;
      if (!container || !container.classList || !container.classList.contains('messages-container')) return;
      if (isAutoScrollingRef.current) return;
      const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      if (distanceFromBottom <= 50) {
        userScrolledUpRef.current = false;
      } else {
        userScrolledUpRef.current = true;
      }
      // Infinite scroll-up: at the very top, pull an older history page.
      // Guards (hasMore / already-loading) live inside loadOlderMessages.
      if (container.scrollTop <= 0) {
        loadOlderMessagesRef.current?.();
      }
    };

    document.addEventListener('wheel', handleWheel, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    return () => {
      document.removeEventListener('wheel', handleWheel);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('scroll', handleScroll, { capture: true });
    };
  }, []);

  // After an older page is prepended, keep the user's viewport anchored on
  // the message they were looking at: shift scrollTop by exactly the height
  // the new content added. Runs before paint so there is no visible jump.
  useLayoutEffect(() => {
    const pending = pendingScrollAdjustRef.current;
    if (!pending) return;
    pendingScrollAdjustRef.current = null;
    const container = document.querySelector('.messages-container');
    if (!container) return;
    isAutoScrollingRef.current = true;
    container.scrollTop = pending.scrollTop + (container.scrollHeight - pending.scrollHeight);
    // Keep the flag long enough for the async scroll event this jump fires,
    // so it isn't mis-read as user scroll (same pattern as anchor/scrollToBottom).
    setTimeout(() => { isAutoScrollingRef.current = false; }, 150);
  }, [messages]);

  // When the typewriter finishes, the feedback (👍/👎) buttons render below
  // the response.
  //   - Short response: scrolling to the bottom keeps the user message visible
  //     AND reveals the feedback row → do it.
  //   - Long response: scrolling to the bottom would push the user message
  //     off-screen → keep the existing anchor (user message at top); the user
  //     can scroll down to reach the feedback row themselves.
  // If the user has already taken over scroll, respect them either way.
  const wasTypingRef = useRef(false);
  useEffect(() => {
    if (typingMessageId) {
      wasTypingRef.current = true;
      return;
    }
    if (!wasTypingRef.current) return; // initial mount, not a typewriter completion
    wasTypingRef.current = false;
    if (userScrolledUpRef.current) return;

    const container = document.querySelector('.messages-container');
    if (!container) return;

    requestAnimationFrame(() => {
      const userMessages = container.querySelectorAll('.message-user');
      const lastUserMsg = userMessages[userMessages.length - 1];
      if (!lastUserMsg) return;

      // Will the user message remain visible if we scroll to the bottom?
      // It stays visible iff (scrollHeight - userMsgTop) <= clientHeight.
      const fitsWithoutHidingUserMsg =
        (container.scrollHeight - lastUserMsg.offsetTop) <= container.clientHeight;

      if (!fitsWithoutHidingUserMsg) return; // long response — keep anchor

      isAutoScrollingRef.current = true;
      container.scrollTop = container.scrollHeight;
      setTimeout(() => { isAutoScrollingRef.current = false; }, 150);
    });
  }, [typingMessageId]);

  // Auto-focus textarea when input becomes available
  useEffect(() => {
    if (!isWaitingForResponse && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isWaitingForResponse]);

  const scrollToBottom = () => {
    try {
      const messagesContainer = document.querySelector('.messages-container');
      if (messagesContainer) {
        // Flag so the scroll listener knows this is programmatic, not user-initiated
        isAutoScrollingRef.current = true;
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
        // Clear the flag after the browser processes the scroll event.
        // Use a small timeout as fallback in case rAF doesn't fire (e.g. hidden tab)
        requestAnimationFrame(() => {
          isAutoScrollingRef.current = false;
        });
        setTimeout(() => {
          isAutoScrollingRef.current = false;
        }, 100);
      }
    } catch (error) {
      logger.warn('CHAT', 'scrollToBottom failed:', error.message);
    }
  };

  // ChatGPT-style: place the most-recent user message near the top of the
  // visible area, so "question on top, answer below" matches user expectation.
  // If the scroll container can't reach that position (short history, not
  // enough content below), the browser clamps to the max scrollable offset.
  const anchorLastUserMessageToTop = () => {
    try {
      const container = document.querySelector('.messages-container');
      if (!container) return;
      const userMessages = container.querySelectorAll('.message-user');
      const lastUserMessage = userMessages[userMessages.length - 1];
      if (!lastUserMessage) return;

      const containerRect = container.getBoundingClientRect();
      const messageRect = lastUserMessage.getBoundingClientRect();
      // Offset of the message relative to the scrollable container's content box.
      const offsetWithinContainer = messageRect.top - containerRect.top + container.scrollTop;
      const target = Math.max(0, offsetWithinContainer - 20); // 20px breathing room

      isAutoScrollingRef.current = true;
      container.scrollTop = target;
      // Keep the flag set for ~150ms so the async scroll event fired by the
      // browser for this programmatic jump is still recognized as auto-scroll
      // and doesn't get mis-classified as user scroll-up (anchor isn't near
      // the bottom, so the scroll handler would otherwise latch that state).
      setTimeout(() => { isAutoScrollingRef.current = false; }, 150);
    } catch (error) {
      logger.warn('CHAT', 'anchorLastUserMessageToTop failed:', error.message);
    }
  };

  // Typewriter effect for assistant messages - character by character like ChatGPT
  // Note: displayedText[messageId] and typingMessageId should be set BEFORE calling this
  // Parameters:
  //   - messageId: The message ID
  //   - textToType: The plain text to show during typing animation
  //   - finalContent: (Optional) The final content to show when typing completes (e.g., HTML). If not provided, uses textToType
  const startTypewriterEffect = (messageId, textToType, finalContent = null) => {
    // Clear any existing interval
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
    }

    let currentIndex = 0;
    const contentToShowWhenDone = finalContent || textToType;

    typingIntervalRef.current = setInterval(() => {
      if (currentIndex < textToType.length) {
        currentIndex++;
        setDisplayedText(prev => ({
          ...prev,
          [messageId]: textToType.substring(0, currentIndex)
        }));
      } else {
        clearInterval(typingIntervalRef.current);
        setTypingMessageId(null);
        // Store the final content (could be HTML or plain text) once typing is complete
        setDisplayedText(prev => ({ ...prev, [messageId]: contentToShowWhenDone }));
      }
    }, 5); // 20ms per character for smooth typing effect
  };

  // Cancel requests when navigating away (location changes)
  useEffect(() => {
    // When location changes, abort any pending requests
    return () => {
      if (abortControllerRef.current) {

logger.debug('CHAT', 'Aborting chat request due to navigation');
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [location.pathname]); // Run cleanup when pathname changes

  // Clean up on unmount - cancel requests and clear intervals
  useEffect(() => {
    return () => {
      // Cancel any pending chat request when component unmounts
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      // Clear typing interval
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
      }
    };
  }, []);

  // Initialize iframe keyboard fix for mobile keyboard handling
  useEffect(() => {
    const cleanup = initIframeKeyboardFix();
    return cleanup;
  }, []);

  // Scroll messages to bottom when visual viewport resizes (keyboard open/close)
  useEffect(() => {
    if (!window.visualViewport) return;

    let prevHeight = window.visualViewport.height;

    const handleViewportResize = () => {
      const currentHeight = window.visualViewport.height;
      // Viewport shrank (keyboard opened) - scroll messages to stay at bottom
      if (currentHeight < prevHeight - 50) {
        requestAnimationFrame(() => {
          const container = document.querySelector('.messages-container');
          if (container) {
            container.scrollTop = container.scrollHeight;
          }
        });
      }
      prevHeight = currentHeight;
    };

    window.visualViewport.addEventListener('resize', handleViewportResize);
    return () => window.visualViewport.removeEventListener('resize', handleViewportResize);
  }, []);


  // Function to render message content with clickable links and formatting
  // ✅ P2-21: Memoize expensive message rendering to prevent recreation on every render
  // Strip indented room image lines (e.g. "\n   Room Image: https://cdn.example.com/room.jpg")
  // These are added by room_formatter for SMS/RCS but break web display
  const stripRoomImageUrls = (text) => text ? text.replace(/\n {3}Room Image: https?:\/\/\S+/g, '') : text;

  const renderMessageContent = useCallback((content, messageId = null, isAssistant = false) => {
    // Check if original content is HTML (from TipTap editor)
    const isHTMLContent = isHTML(content);

    // For HTML content during typing animation: show plain text
    // After typing completes: show formatted HTML
    const textToRender = stripRoomImageUrls(
      (isAssistant && messageId && displayedText[messageId] !== undefined)
        ? displayedText[messageId]
        : content
    );

    // If it's HTML content and typing is complete (textToRender === content), render as HTML
    if (isHTMLContent && textToRender === content) {
      // Sanitize HTML to prevent XSS attacks. Uses the centralized
      // sanitizer with explicit URL-scheme whitelist (blocks javascript:,
      // vbscript:, data:text/html — preserves http/https/mailto/tel/sms,
      // data:image/*, and relative paths).
      const sanitizedHTML = sanitizeHTML(content);

      return <div dangerouslySetInnerHTML={{ __html: sanitizedHTML }} className="html-content" />;
    }

    // For assistant messages, apply markdown formatting first
    if (isAssistant) {
      const formattedSegments = formatMarkdownText(textToRender) || [{ type: 'normal', content: textToRender || '' }];

      // The link to the Google Calendar page becomes a connect button. While it
      // is still being typed it is only a prefix, so keep it hidden until then.
      const calendarLink = findGoogleCalendarPageLink(content);
      const isCalendarLinkBeingTyped = (part) =>
        calendarLink !== null &&
        calendarLink.startsWith(part) &&
        (textToRender || '').trimEnd().endsWith(part);

      return formattedSegments.map((segment, segIndex) => {
        // Check for URLs in the segment content
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const parts = segment.content.split(urlRegex);

        const renderedParts = parts.map((part, partIndex) => {
          if (part.match(urlRegex)) {
            if (isGoogleCalendarPageLink(part)) {
              return <ConnectCalendarButton key={`${segIndex}-${partIndex}`} />;
            }
            if (isCalendarLinkBeingTyped(part)) {
              return null;
            }

            // Check if it's an image URL
            const imageExtensions = /\.(jpg|jpeg|png|gif|webp|svg)$/i;
            const isImage = imageExtensions.test(part);

            if (isImage) {
              return (
                <div key={`${segIndex}-${partIndex}`} className="message-image-container">
                  <a href={part} target="_blank" rel="noopener noreferrer" className="message-image-link">
                    <img src={part} alt="Shared attachment" className="message-image" />
                    <span className="image-link-text">Click to view full size</span>
                  </a>
                </div>
              );
            } else {
              // Regular link
              return (
                <a key={`${segIndex}-${partIndex}`} href={part} target="_blank" rel="noopener noreferrer" className="message-link">
                  {part}
                </a>
              );
            }
          }

          // FILTER OUT markdown image syntax - comment out raw markdown display
          // Skip rendering if this part looks like markdown image syntax (e.g., "![" or "]")
          const markdownImagePattern = /^!?\[|^\]$/;
          if (markdownImagePattern.test(part.trim())) {
            return null; // Don't render markdown syntax
          }

          // Around the calendar button: drop the "👉" pointing at the link and
          // the blank lines either side — the button sits on its own line with
          // its own spacing, so they would only leave empty lines around it
          const nextPart = parts[partIndex + 1];
          const prevPart = parts[partIndex - 1];
          let text = part;
          if (nextPart !== undefined &&
            (isGoogleCalendarPageLink(nextPart) || isCalendarLinkBeingTyped(nextPart))) {
            text = stripPointerBeforeLink(text).trimEnd();
          }
          if (prevPart !== undefined && isGoogleCalendarPageLink(prevPart)) {
            text = text.trimStart();
          }

          // Apply formatting based on segment type
          const key = `${segIndex}-${partIndex}`;
          switch (segment.type) {
            case 'bold':
              return <strong key={key} className="formatted-text-bold">{text}</strong>;
            case 'larger':
              return <span key={key} className="formatted-text-larger">{text}</span>;
            default:
              return <span key={key}>{text}</span>;
          }
        });

        return renderedParts;
      }).flat();
    } else {
      // For user messages, just handle URLs without formatting.
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const imageExtensions = /\.(jpg|jpeg|png|gif|webp|svg)$/i;
      const markdownImagePattern = /^!?\[|^\]$/;
      const parts = textToRender.split(urlRegex);

      // Collect every image URL up-front so the lightbox can navigate the
      // full set when one is clicked, regardless of how many text parts
      // sit between them.
      const userImageUrls = parts.filter(
        p => urlRegex.test(p) && imageExtensions.test(p)
      );
      urlRegex.lastIndex = 0;

      // Walk `parts` and accumulate runs of consecutive image URLs into a
      // single `.message-image-gallery` block. Text and non-image URLs
      // flush the current run. Whitespace between consecutive images is
      // preserved INSIDE the gallery (it does not break the run).
      const out = [];
      let imageGroup = [];

      const flushGroup = () => {
        if (imageGroup.length === 0) return;
        out.push(
          <div key={`gallery-${out.length}`} className="message-image-gallery">
            {imageGroup}
          </div>
        );
        imageGroup = [];
      };

      parts.forEach((part, index) => {
        if (part.match(urlRegex)) {
          const isImage = imageExtensions.test(part);
          if (isImage) {
            const imgIndex = userImageUrls.indexOf(part);
            imageGroup.push(
              <a
                key={`img-${index}`}
                href={part}
                target="_blank"
                rel="noopener noreferrer"
                className="message-image-link"
                onClick={(e) => {
                  e.preventDefault();
                  openLightbox(userImageUrls, Math.max(0, imgIndex));
                }}
              >
                <img src={part} alt="Shared attachment" className="message-image" />
                <span className="image-link-text">Click to view full size</span>
              </a>
            );
            return;
          }
          // Non-image URL — flush any pending images then render link.
          flushGroup();
          out.push(
            <a key={part} href={part} target="_blank" rel="noopener noreferrer" className="message-link">
              {part}
            </a>
          );
          return;
        }

        // Text part. Pure whitespace between consecutive image URLs is
        // dropped so the gallery stays a clean row.
        if (imageGroup.length > 0 && part.trim() === '') return;

        flushGroup();
        if (markdownImagePattern.test(part.trim())) return;
        out.push(<span key={`text-${index}-${part.substring(0, 20)}`}>{part}</span>);
      });

      flushGroup();
      return out;
    }
  }, [displayedText, openLightbox]); // CRITICAL: Must include displayedText for typewriter effect to work

  // Add typing dots for typing messages
  const renderTypingIndicator = (messageId) => {
    if (typingMessageId === messageId) {
      return <span className="typing-dots">...</span>;
    }
    return null;
  };

  // Files under a reply: support-ticket media or FAQ attachments.
  // Images → grid (click opens full size), videos/documents → icon + link rows.
  // Named by TYPE ("PDF", "Image 2"), never by the original file name.
  const renderAttachmentBlock = (attachments, keyPrefix) => {
    if (!attachments || attachments.length === 0) return null;
    const images = attachments.filter(a => a?.type === 'image' && a.url);
    const files = attachments.filter(a => a?.url && a.type !== 'image');
    const fileNames = attachmentDisplayNames(files);
    return (
      <div className="attachment-card">
        {images.length > 0 && (
          <div className={`attachment-grid attachment-grid-${Math.min(images.length, 4)}`}>
            {images.map((attachment, idx) => (
              <a
                key={`img-${keyPrefix}-${idx}`}
                href={attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                className="attachment-grid-item"
                title={attachmentDisplayName(attachment)}
              >
                <img
                  src={attachment.url}
                  alt={attachmentDisplayName(attachment)}
                  onError={(e) => { e.target.src = ''; e.target.alt = 'Image not found'; }}
                />
              </a>
            ))}
          </div>
        )}
        {files.map((attachment, idx) => (
          <div key={`file-${keyPrefix}-${idx}`} className="attachment-preview">
            <span className="attachment-icon">{attachment.type === 'video' ? '🎥' : '📎'}</span>
            <a href={attachment.url} target="_blank" rel="noopener noreferrer" className="attachment-link">
              {fileNames[idx]}
            </a>
          </div>
        ))}
      </div>
    );
  };

  // FAQ reply as answer blocks: bold question (multi-part only) → answer → that
  // block's files. Backend sets faq_segments only when at least one file exists.
  const renderFaqSegments = (message) => (
    <div className="faq-segments">
      {message.faq_segments.map((segment, idx) => (
        <div key={`seg-${message.id}-${idx}`} className="faq-segment">
          {segment.question && (
            <div className="faq-segment-question"><strong>{segment.question}</strong></div>
          )}
          {renderMessageContent(segment.text, null, true)}
          {renderAttachmentBlock(segment.attachments, `${message.id}-seg-${idx}`)}
        </div>
      ))}
    </div>
  );

  const hasFaqSegments = (message) =>
    Array.isArray(message.faq_segments) && message.faq_segments.length > 0;

  // Render room selection cards with images
  // ✅ P2-21: Memoize room selection rendering to prevent recreation on every render
  const renderRoomSelection = useCallback((rooms) => {
    if (!rooms || rooms.length === 0) return null;

    // Backend numbers rooms persistently across pages (page 2 → 4, 5, 6…).
    // Each room carries its own number; fall back to positional if absent.
    const getRoomNumber = (room, index) =>
      room?.number ?? room?.display_number ?? room?.position ?? index + 1;

    const handleRoomSelect = (index) => {
      // Send the room NAME as a message (more intuitive than numbers)
      const room = rooms[index];
      const roomName = room?.name || String(index + 1); // Fallback to number if no name
      setNewMessage(roomName);
      // Trigger send immediately
      setTimeout(() => {
        if (textareaRef.current) {
          const syntheticEvent = new Event('submit', { bubbles: true, cancelable: true });
          textareaRef.current.form.dispatchEvent(syntheticEvent);
        }
      }, 100);
    };

    return (
      <div className="room-selection-wrapper">
        <div className="room-selection-container">
          {rooms.map((room, index) => (
            <div
              key={room.id || index}
              className="room-card"
              onClick={() => handleRoomSelect(index)}
            >
              <div className="room-image-wrapper">
                {room.image ? (
                  <img
                    src={room.image}
                    alt={room.name}
                    className="room-card-image"
                    onLoad={() => {
                      // Don't jump to bottom on image load — that would push
                      // the user's question out of view. Keep anchor intact.
                      if (!userScrolledUpRef.current) anchorLastUserMessageToTop();
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const placeholder = e.target.nextElementSibling;
                      if (placeholder && placeholder.classList.contains('room-image-placeholder')) {
                        placeholder.style.display = 'flex';
                      }
                    }}
                  />
                ) : null}
                {/* Fallback placeholder when image fails or doesn't exist */}
                <div className="room-image-placeholder" style={{ display: room.image ? 'none' : 'flex' }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                    <path d="M3 15L8 10L12 14L16 10L21 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                  </svg>
                  <span>No image</span>
                </div>
                <div className="room-card-click-indicator">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" fill="rgba(255,255,255,0.9)" stroke="currentColor" strokeWidth="2"/>
                    <path d="M8 12L12 16L16 12M12 8V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
              <div className="room-card-content">
                <h4 className="room-card-title">{getRoomNumber(room, index)}. {room.name}</h4>
                <p className="room-card-capacity">Capacity: {room.capacity} people</p>
                {room.rate && (
                  <p className="room-card-rate">
                    ${room.rate.price?.toFixed(2)} / {room.rate.intervalLength}, {room.rate.name}
                  </p>
                )}
                {(() => {
                  const amenities = room.amenity_titles || room.amenities || room.amenityTitles;
                  if (!amenities || amenities.length === 0) return null;
                  return (
                    <div className="room-card-amenities">
                      {amenities.map((amenity) => (
                        <span key={amenity} className="room-amenity-tag">{amenity}</span>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>
        <div className="room-selection-instruction">
          💡 Click on a room card or type its number ({
            (() => {
              const nums = rooms.map((room, index) => getRoomNumber(room, index));
              if (nums.length === 1) return String(nums[0]);
              if (nums.length === 2) return `${nums[0]} or ${nums[1]}`;
              return nums.slice(0, -1).join(', ') + ', or ' + nums[nums.length - 1];
            })()
          }) to select
          <br />
          <span className="room-selection-hint">Need changes? Try 'change to 3-4pm', '5 people', or 'tomorrow'</span>
        </div>
      </div>
    );
  }, []); // No dependencies - uses setState and dispatches synthetic event

  const loadMessages = async () => {
    setLoading(true);

    try {
      const data = await chatAPI.getMessages({ limit: INITIAL_HISTORY_LIMIT, offset: 0 });

logger.debug('CHAT', 'Loaded messages:', data);
      // Handle both array response and object with items property
      const messagesList = Array.isArray(data) ? data : (data.items || []);

      // History pagination bookkeeping: a full initial window means there may
      // be older messages to load when the user scrolls to the top.
      historyOffsetRef.current = messagesList.length;
      hasMoreHistoryRef.current = messagesList.length >= INITIAL_HISTORY_LIMIT;

      // Debug: Check if messages have user_image field and channel info
      if (process.env.NODE_ENV !== 'production') {
        logger.debug('CHAT', 'Messages with channel info:', messagesList.map(m => ({
          role: m.role,
          channel: m.channel,
          user_image: m.user_image,
          ai_assistant_image: m.ai_assistant_image
        })));
      }
      
      setMessages(messagesList);

      // Restore quick-reply chips from the most recent assistant message (if any)
      const lastAssistant = [...messagesList].reverse().find(m => m.role === 'assistant');
      setWebChips(Array.isArray(lastAssistant?.web_chips) ? lastAssistant.web_chips : []);

      // Check if user is in the middle of creating a support ticket (restore state after reload)
      if (messagesList.length > 0) {
        const lastMessage = messagesList[messagesList.length - 1];
        if (lastMessage.role === 'assistant') {
          // Did the last message end the ticket flow? Reads raw_output.ticket_id /
          // show_upload, so a reload after an AI-worded success no longer restores
          // the uploader. See utils/ticketFlowSignals.js.
          const messageContent = lastMessage.content || '';
          const ticketCompleted = isTicketFlowEnded(lastMessage, messageContent);

          // Check if last message indicates ticket was cancelled
          const ticketCancelled = isTicketCancelled(lastMessage, messageContent);

          if (ticketCompleted) {
            // Ticket already completed, don't show uploader
            logger.info('CHAT', 'Ticket already completed, NOT restoring attachment uploader', {
              messagePreview: messageContent.substring(0, 50)
            });
          } else if (ticketCancelled) {
            // Ticket was cancelled, don't show uploader
            logger.info('CHAT', 'Ticket was cancelled, NOT restoring attachment uploader', {
              messagePreview: messageContent.substring(0, 50),
              exit_requested: lastMessage.entities?.exit_requested,
              exit_type: lastMessage.entities?.exit_type
            });
          } else {
            // Show uploader if:
            // 1. Waiting for confirmation (user needs to say "yes")
            // 2. OR asking for issue details (user just triggered ticket with "wifi broken")
            const isAwaitingConfirm = lastMessage.conversation_state === 'ticket_awaiting_confirm';
            const isAwaitingDetails = lastMessage.intent === 'report_issue' &&
                                     lastMessage.conversation_state !== 'complete';

            if (isAwaitingConfirm || isAwaitingDetails) {
              setIsCreatingTicket(true);
              logger.info('CHAT', 'Restored ticket creation state after page reload', {
                isAwaitingConfirm,
                isAwaitingDetails,
                conversation_state: lastMessage.conversation_state,
                intent: lastMessage.intent
              });
            }
          }
        }
      }

      // Set displayed text for all existing messages (no typewriter effect for loaded messages)
      const existingDisplayedText = {};
      messagesList.forEach(msg => {
        if (msg.role === 'assistant') {
          // For card-type messages, show only the header (not the full list)
          const cardText = getCardTypewriterText(msg);
          existingDisplayedText[msg.id] = cardText !== null ? cardText : msg.content;
        }
      });
      setDisplayedText(existingDisplayedText);

      // Load existing feedback for these messages (user's own feedback only)
      try {
        const feedbackMap = await feedbackAPI.getMyFeedback();

logger.debug('CHAT', 'Loaded existing feedback:', feedbackMap);

        // Convert feedback map to state format — only include user's own feedback
        const feedbackState = {};
        Object.entries(feedbackMap).forEach(([messageId, value]) => {
          // If backend returns {rating, source}, filter to user-only
          // If backend returns just rating string, accept it (assume it's user's)
          if (typeof value === 'object' && value !== null) {
            if (value.source && value.source !== 'user') return; // Skip admin/QA feedback
            feedbackState[messageId] = {
              rating: value.rating || value,
              submitted: true
            };
          } else {
            feedbackState[messageId] = {
              rating: value,
              submitted: true
            };
          }
        });
        setMessageFeedback(feedbackState);
      } catch (feedbackError) {

logger.error('CHAT', 'Failed to load existing feedback:', feedbackError);
        // Don't show error to user - not critical
      }

      // Don't show error for empty conversations - this is normal for new users
      if (messagesList.length === 0) {

logger.debug('CHAT', 'No messages found - new conversation');
      }
    } catch (error) {

// Only show error toast for actual failures, not empty conversations
      if (error.response?.status !== 404) {
        handleApiError(error, 'CHAT', 'Failed to load messages');
      }
    } finally {
      setLoading(false);

      // Scroll to bottom after loading completes
      // Extra delay to ensure all messages and images are rendered
      setTimeout(() => {
        scrollToBottom();
      }, 300);
    }
  };

  // Infinite scroll-up: fetch the next older history page and prepend it,
  // preserving the scroll position (same UX as Live Conversations).
  // Uses refs (not state) for its guards so the mount-once document scroll
  // listener always sees current values.
  const loadOlderMessages = async () => {
    if (loadingOlderRef.current || !hasMoreHistoryRef.current) return;
    loadingOlderRef.current = true;
    setLoadingOlder(true);

    const container = document.querySelector('.messages-container');
    const scrollSnapshot = container
      ? { scrollTop: container.scrollTop, scrollHeight: container.scrollHeight }
      : null;

    try {
      const data = await chatAPI.getMessages({
        limit: OLDER_HISTORY_PAGE_SIZE,
        offset: historyOffsetRef.current
      });
      const older = Array.isArray(data) ? data : (data.items || []);

      // A short page means we've reached the start of the conversation.
      hasMoreHistoryRef.current = older.length >= OLDER_HISTORY_PAGE_SIZE;
      historyOffsetRef.current += older.length;

      if (older.length > 0) {
        // Prepended assistant messages render instantly (no typewriter);
        // card-type messages show only their header, like on initial load.
        setDisplayedText(prev => {
          const next = { ...prev };
          older.forEach(msg => {
            if (msg.role === 'assistant' && next[msg.id] === undefined) {
              const cardText = getCardTypewriterText(msg);
              next[msg.id] = cardText !== null ? cardText : msg.content;
            }
          });
          return next;
        });

        pendingScrollAdjustRef.current = scrollSnapshot;
        setMessages(prev => {
          // Dedupe by id: sending messages mid-paging shifts the offset
          // window, so an older page can overlap what's already loaded.
          const seen = new Set(prev.map(m => m.id));
          const fresh = older.filter(m => !seen.has(m.id));
          // Always return a new array — the layout effect keyed on [messages]
          // must fire to consume pendingScrollAdjustRef even when the whole
          // page was duplicates.
          return [...fresh, ...prev];
        });
      }
    } catch (error) {
      handleApiError(error, 'CHAT', 'Failed to load older messages');
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  };
  loadOlderMessagesRef.current = loadOlderMessages;

  const clearHistory = async () => {
    if (!window.confirm('Are you sure you want to clear all chat history? This cannot be undone.')) {
      return;
    }

    setClearingHistory(true);
    try {
      await chatAPI.clearHistory();
      setMessages([]);
      setDisplayedText({});
      setWebChips([]);
      historyOffsetRef.current = 0;
      hasMoreHistoryRef.current = false;
      toast.success('Chat history cleared');
    } catch (error) {
      handleApiError(error, 'CHAT', 'Failed to clear history');
    } finally {
      setClearingHistory(false);
      setShowSettings(false);
    }
  };

  // P2-21: Not memoized - references sendMessage which is defined later
  // This is a small function and doesn't cause performance issues
  const handleKeyDown = (e) => {
    // Submit on Enter, new line on Shift+Enter
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Only send if not waiting for response and has content
      if (!isWaitingForResponse && newMessage.trim()) {
        sendMessage(e);
      }
    }
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Calculate new height (max 200px)
      const newHeight = Math.min(textarea.scrollHeight, 200);
      textarea.style.height = `${newHeight}px`;
    }
  };

  // Handle thumbs up feedback
  const handleThumbsUp = async (messageId) => {
    try {
      setMessageFeedback(prev => ({
        ...prev,
        [messageId]: { ...prev[messageId], submitting: true }
      }));

      await feedbackAPI.submitFeedback(messageId, {
        rating: 'thumbs_up'
      });

      setMessageFeedback(prev => ({
        ...prev,
        [messageId]: { rating: 'thumbs_up', submitted: true }
      }));

      toast.success('Thank you for your feedback!');
    } catch (error) {
      // Check if feedback already exists (could be admin/QA submitted — don't block user)
      const status = error.response?.status;
      const errorDetail = error.response?.data?.detail || '';
      const errorMessage = error.message || '';

      if (status === 400 && (errorDetail.toLowerCase().includes('already') || errorMessage.toLowerCase().includes('already'))) {
        // User already submitted their own feedback — mark as submitted
        setMessageFeedback(prev => ({
          ...prev,
          [messageId]: { rating: 'thumbs_up', submitted: true, alreadyExists: true }
        }));
        toast.success('Thank you for your feedback!');
      } else {
        handleApiError(error, 'CHAT', 'Failed to submit feedback');
        setMessageFeedback(prev => ({
          ...prev,
          [messageId]: { ...prev[messageId], submitting: false }
        }));
      }
    }
  };

  // Handle thumbs down - show text box
  const handleThumbsDown = (messageId) => {
    setMessageFeedback(prev => ({
      ...prev,
      [messageId]: {
        ...prev[messageId],
        rating: 'thumbs_down',
        showTextBox: true,
        feedbackText: ''
      }
    }));
  };

  // Submit thumbs down with optional text
  const submitThumbsDown = async (messageId) => {
    const feedback = messageFeedback[messageId];

    try {
      setMessageFeedback(prev => ({
        ...prev,
        [messageId]: { ...prev[messageId], submitting: true }
      }));

      await feedbackAPI.submitFeedback(messageId, {
        rating: 'thumbs_down',
        feedback_text: feedback?.feedbackText || null
      });

      setMessageFeedback(prev => ({
        ...prev,
        [messageId]: {
          rating: 'thumbs_down',
          submitted: true,
          feedbackText: feedback?.feedbackText
        }
      }));

      toast.success('Thank you for your feedback!');
    } catch (error) {
      // Check if feedback already exists (could be admin/QA submitted — don't block user)
      const status = error.response?.status;
      const errorDetail = error.response?.data?.detail || '';
      const errorMessage = error.message || '';

      if (status === 400 && (errorDetail.toLowerCase().includes('already') || errorMessage.toLowerCase().includes('already'))) {
        // User already submitted their own feedback — mark as submitted
        setMessageFeedback(prev => ({
          ...prev,
          [messageId]: {
            rating: 'thumbs_down',
            submitted: true,
            alreadyExists: true,
            feedbackText: feedback?.feedbackText
          }
        }));
        toast.success('Thank you for your feedback!');
      } else {
        handleApiError(error, 'CHAT', 'Failed to submit feedback');
        setMessageFeedback(prev => ({
          ...prev,
          [messageId]: { ...prev[messageId], submitting: false }
        }));
      }
    }
  };

  // Cancel thumbs down
  const cancelFeedback = (messageId) => {
    setMessageFeedback(prev => {
      const newState = { ...prev };
      delete newState[messageId];
      return newState;
    });
  };

  const handleChipSelect = (value) => {
    if (!value || isWaitingForResponse) return;
    sendMessage(null, value);
  };

  // Tap "Book Meeting Room" on a calendar event card.
  // Shows a friendly user bubble (with the event title) but sends the
  // plain "Book Meeting Room" message + button_payload to the backend,
  // which then routes through the existing room_select chip flow.
  const handleBookCalendarRoom = (event) => {
    if (!event?.id || isWaitingForResponse) return;
    const title = (event.title || '').trim();
    const displayText = title
      ? `Book Meeting Room for ${title}`
      : 'Book Meeting Room';
    sendMessage(null, 'Book Meeting Room', `book_calendar_event_${event.id}`, displayText);
  };

  // Onboarding gate (A/B): after the user completes phone verification in the
  // in-chat modal. checkAuth() refreshes the user: if admin approval is
  // required the App-level effect redirects to /pending-approval; otherwise
  // the user was auto-approved and the gate-sync effect unlocks the composer.
  const handleVerificationComplete = async () => {
    if (checkAuth) {
      await checkAuth();
    }
    setShowVerifyModal(false);
  };

  const sendMessage = async (e, overrideText, buttonPayload, displayText) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();

    // Onboarding gate (A/B): composer is locked until the phone is verified.
    // The backend enforces this too — this guard just keeps the UX clean.
    if (verificationGated) {
      setShowVerifyModal(true);
      return;
    }

    // `attachments` is sourced from the active scope's bucket (ticket or
    // CA), so cross-flow leaks are prevented at the state layer — no
    // runtime gating needed here. When neither flow is active, the active
    // bucket is null and `attachments` is the empty array.
    const hasAttachmentsToSend = attachments && attachments.length > 0;

    // Text comes from the typed message OR from a chip click override
    const baseText = typeof overrideText === 'string' ? overrideText : newMessage;

    // Allow sending if: (has text message) OR (has attachments available)
    const canSend = baseText.trim() || hasAttachmentsToSend;

    if (!canSend || isWaitingForResponse) {
      logger.debug('CHAT', 'Cannot send message', {
        hasText: !!baseText.trim(),
        hasAttachments: hasAttachmentsToSend,
        isWaiting: isWaitingForResponse
      });
      return;
    }

    // Hide chips immediately — they belong to the previous turn.
    setWebChips([]);

    // User is actively sending — reset scroll lock and flag that the next
    // messages.length change should anchor the user's message near the top
    // (ChatGPT-style: question visible on top, answer streams in below).
    userScrolledUpRef.current = false;
    shouldAnchorUserMessageRef.current = true;

    let messageContent = baseText.trim();

    // Strip upload triggers (e.g. "+image") before the text reaches the
    // backend or the user's own bubble. No-op when no trigger is active.
    messageContent = uploadTrigger.sanitize(messageContent);

    // If sending attachments without text, add a default message
    if (!messageContent && hasAttachmentsToSend) {
      messageContent = '[Uploaded attachments]';
      logger.debug('CHAT', 'Adding default message for attachment-only send');
    }

    // Files come from the active scope's bucket (ticket or CA). When no
    // flow is active the bucket is empty by construction — no possibility
    // of stale state from another scope leaking in.
    let attachmentsToSend = null;
    if (attachments && attachments.length > 0) {
      attachmentsToSend = attachments;
      logger.info('CHAT', '✅ Sending attachments with message', {
        message: messageContent,
        count: attachments.length,
        scope: isCreatingTicket ? 'ticket' : 'ca',
      });
    } else {
      logger.debug('CHAT', 'No attachments to send');
    }

    // Optimistic UI: for one-shot CA sends, clear the CA bucket BEFORE
    // the network round-trip so the thumbnails + the `+` upload icon
    // vanish from the input box the moment the user clicks send — not
    // after the backend responds. The `attachmentsToSend` snapshot above
    // is already captured, so the send itself still carries the files.
    // Ticket flow is left alone — it accumulates across multiple messages
    // and clears only on ticket completion/cancel.
    if (attachmentsToSend && !isCreatingTicket) {
      caUpload.clearAttachments();
    }

    setNewMessage('');
    setSendingMessage(true);
    setIsWaitingForResponse(true);

    // Reset textarea height after clearing
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    // Add user message to UI immediately (with attachment preview if present).
    // When a chip/button provides a friendlier displayText (e.g. "Book Meeting
    // Room for Team Sync"), show that in the bubble — the wire `messageContent`
    // still goes to the backend unchanged.
    const userBubbleContent =
      typeof displayText === 'string' && displayText.trim()
        ? displayText.trim()
        : messageContent;
    const userMessage = {
      // Unique id: Date.now() alone collides when two messages are sent within
      // the same millisecond, causing duplicate React keys. Add a random suffix.
      id: `u-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      role: 'user',
      content: userBubbleContent,
      channel: 'web',
      created_at: new Date().toISOString(),
      user_image: user?.user_image || null,
      attachments: null // Attachments shown on assistant's confirmation response instead
    };
    setMessages(prev => [...prev, userMessage]);

    // Snapshot of the outbound text for the onboarding-gate branch below.
    // (The response handler re-declares `messageContent` in its own block, so
    // the gate branch cannot read this variable directly without hitting TDZ.)
    const sentMessageText = messageContent;

    // Create abort controller for this request
    abortControllerRef.current = new AbortController();

    try {

logger.info('CHAT', '🚀 About to call API with:', {
        message: messageContent,
        hasAttachments: !!attachmentsToSend,
        attachmentsCount: attachmentsToSend ? attachmentsToSend.length : 0,
        attachmentsData: attachmentsToSend
      });

      const response = await chatAPI.sendMessage(messageContent, abortControllerRef.current.signal, attachmentsToSend, buttonPayload);

logger.debug('CHAT', 'Raw message response:', response);
      
      // Handle backend response format
      if (response) {
        // Onboarding gate (A/B): the backend blocked this message. Don't render
        // the gate text as a chat bubble (the banner + modal already carry it,
        // and the backend didn't persist anything for this turn). The user's
        // text must NOT be lost: drop the optimistic bubble (it was never
        // accepted), put the text back in the composer, and stash it in
        // sessionStorage so it survives the pending-approval redirect — the
        // restore effect refills the input once the user may chat again.
        if (response.verification_required === true) {
          setMessages(prev => prev.filter(m => m.id !== userMessage.id));
          setNewMessage(sentMessageText);
          try {
            sessionStorage.setItem('gate_blocked_message', sentMessageText);
          } catch (_) { /* storage full/blocked — composer restore still applies */ }
          setVerificationGated(true);
          setShowVerifyModal(true);
          logger.info('CHAT', 'Onboarding gate: verification required, message preserved in composer');
          return; // finally-block below resets the sending state
        }

        // V1 Pattern-based format: { success: bool, output: string, intent: string, function_name: string, layer_used: string, time_ms: float }

        // Determine which format we're using
        const isV1Format = response.output !== undefined;
        let messageContent = isV1Format ? response.output : response.response;

        // If raw_output.debug exists, strip embedded >> [STATE DEBUG] from text
        // to avoid double display (structured <pre> block will show it instead)
        if (response.raw_output?.debug && messageContent) {
          messageContent = messageContent.replace(/\n*\s*>> \[STATE DEBUG\][\s\S]*$/, '').trim();
        }

        if (messageContent && typeof messageContent === 'string') {
          logger.debug('CHAT', isV1Format ? 'V1 pattern-based format detected' : 'Old AI format detected');

          const assistantMessage = {
            id: response.message_id || Date.now() + 1, // Use real DB ID for feedback
            role: 'assistant',
            content: messageContent,
            channel: 'web',
            created_at: new Date().toISOString(),
            intent: response.intent,
            rooms: response.rooms || null, // Add rooms array for room selection display
            extra_metadata: response.extra_metadata || null, // Contains room_image, room_name from backend
            attachments: response.attachments || null, // Support ticket / FAQ attachments
            faq_segments: response.faq_segments || null, // FAQ answer blocks, each with its own files
            function_name: response.function_name || null, // Always store for event detection
            raw_output: response.raw_output || null, // Structured data for event cards
            events: response.events || null, // Top-level organization-event array (image contract: events[].image)
            web_chips: Array.isArray(response.web_chips) ? response.web_chips : [],
            // V1-specific fields
            ...(isV1Format && {
              layer_used: response.layer_used,
              time_ms: response.time_ms,
              confidence: response.confidence
            })
          };

          // Surface the new chips above the input (empty array = nothing rendered).
          setWebChips(assistantMessage.web_chips);

          // CRITICAL: Initialize displayedText BEFORE adding message to prevent flash of full content
          setDisplayedText(prev => ({ ...prev, [assistantMessage.id]: '' }));
          setTypingMessageId(assistantMessage.id);

          // Only add assistant message (user message already added on line 428)
          setMessages(prev => [...prev, assistantMessage]);

          // Start typewriter effect — card types show only the header
          const cardText = getCardTypewriterText(assistantMessage);
          let textToType = cardText !== null ? cardText : messageContent;

          // If content is HTML, strip tags for typing animation (prevents showing raw HTML)
          // But keep the original HTML to display when typing completes
          const isHTMLContent = isHTML(textToType);
          const finalContent = isHTMLContent ? textToType : null;
          if (isHTMLContent) {
            textToType = stripHTML(textToType);
          }

          startTypewriterEffect(assistantMessage.id, textToType, finalContent);

          // Did this turn end the ticket flow? Structured signal (raw_output
          // .ticket_id / .show_upload), NOT the assistant's wording — the AI
          // humanizer writes a different success line every time.
          const ticketFlowEnded = isTicketFlowEnded(response, messageContent);

          // Check if ticket was cancelled (using helper function)
          // Backend sends these messages when user cancels: "Ok, cancelled.", "No problem, ticket cancelled.", "Got it. I won't submit the ticket."
          const ticketCancelled = isTicketCancelled(response, messageContent) && isCreatingTicket;

          // Detect if user is in ticket creation flow (using backend response data, NOT message content)
          if (ticketFlowEnded && isCreatingTicket) {
            // Ticket submitted (or ended), hide uploader immediately
            setIsCreatingTicket(false);
            setAttachmentsSent(false);
            clearAttachments();
            logger.info('CHAT', '❌ HIDING attachment uploader', {
              reason: 'Ticket flow ended (raw_output signal)',
              messagePreview: messageContent.substring(0, 50)
            });
          } else if (ticketCancelled) {
            // Ticket was cancelled by user, hide uploader and clear attachments
            setIsCreatingTicket(false);
            setAttachmentsSent(false);
            clearAttachments();
            logger.info('CHAT', '❌ HIDING attachment uploader', {
              reason: 'Ticket cancelled by user (exit_requested=true)',
              exit_type: response.entities?.exit_type,
              messagePreview: messageContent.substring(0, 50)
            });
          } else if (response.intent === 'report_issue' || response.conversation_state === 'ticket_awaiting_confirm') {
            setIsCreatingTicket(true);
            logger.info('CHAT', '✅ SHOWING attachment uploader', {
              reason: response.intent === 'report_issue' ? 'intent=report_issue' : 'state=ticket_awaiting_confirm',
              intent: response.intent,
              conversation_state: response.conversation_state
            });
          } else if (isCreatingTicket) {
            // User completed or canceled ticket, clear state
            setIsCreatingTicket(false);
            setAttachmentsSent(false); // Reset flag for next ticket
            clearAttachments();
            logger.info('CHAT', '❌ HIDING attachment uploader', {
              reason: 'Ticket complete or cancelled (no active state)',
              intent: response.intent,
              conversation_state: response.conversation_state
            });
          }

          // Log v1 performance if available
          if (isV1Format && response.time_ms !== undefined && response.time_ms !== null) {
            if (process.env.NODE_ENV !== 'production') {
              logger.debug('CHAT', `[V1] Detected via ${response.layer_used} in ${response.time_ms.toFixed(2)}ms`);
            }
          }
        }
        // Handle message object response
        else if (response.role && response.content) {

logger.debug('CHAT', 'Direct message object');
          const assistantMessage = {
            ...response,
            channel: response.channel || 'web'
          };

          // CRITICAL: Initialize displayedText BEFORE adding message to prevent flash of full content
          if (response.role === 'assistant') {
            setDisplayedText(prev => ({ ...prev, [response.id]: '' }));
            setTypingMessageId(response.id);
          }

          // Only add assistant message (user message already added on line 428)
          setMessages(prev => [...prev, assistantMessage]);
          if (response.role === 'assistant') {
            // Card types show only the header
            const cardText = getCardTypewriterText(assistantMessage);
            let textToType = cardText !== null ? cardText : response.content;

            // If content is HTML, strip tags for typing animation (prevents showing raw HTML)
            // But keep the original HTML to display when typing completes
            const isHTMLContent = isHTML(textToType);
            const finalContent = isHTMLContent ? textToType : null;
            if (isHTMLContent) {
              textToType = stripHTML(textToType);
            }

            startTypewriterEffect(response.id, textToType, finalContent);
          }
        }
        else {

logger.warn('CHAT', 'Unknown response format:', response);
          // Try to use the response as is
          const assistantMessage = {
            id: Date.now() + 1,
            role: 'assistant',
            content: JSON.stringify(response),
            channel: 'web',
            created_at: new Date().toISOString(),
          };
          // Only add assistant message (user message already added on line 428)
          setMessages(prev => [...prev, assistantMessage]);
        }
      }
    } catch (error) {
      // Check if request was aborted (user navigated away)
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED' || error.name === 'AbortError') {

logger.debug('CHAT', 'Chat request was canceled due to navigation');
        // Don't show error toast for canceled requests
        return;
      }

handleApiError(error, 'CHAT', 'Failed to send message');
      // Remove the user message on error using functional update to avoid stale closure
      setMessages(prev => prev.slice(0, -1));
      setNewMessage(messageContent);
    } finally {
      // Clear abort controller reference
      abortControllerRef.current = null;
      setSendingMessage(false);
      setIsWaitingForResponse(false);
    }
  };

  // The chips row lives inside the absolutely-positioned .message-input-form,
  // so it grows the bottom overlay without taking any layout space. Flag the
  // scroll container so it can add the matching clearance — only for backend
  // chips, since the starter-chip case has no message text to bury.
  const chipsOverlayActive = webChips.length > 0;

  return (
    <div className="chat-container">
      {/* Background gradient effects */}
      <div className="chat-background-effects">
        <div className="chat-gradient-blob chat-gradient-primary"></div>
        <div className="chat-gradient-blob chat-gradient-accent"></div>
      </div>

      {/* Settings Menu */}
      {showSettings && (
        <div className="settings-dropdown">
          <div className="settings-header">
            <h4>Settings</h4>
            <button 
              onClick={() => setShowSettings(false)}
              className="settings-close"
              aria-label="Close settings"
            >
              ×
            </button>
          </div>
          <div className="settings-content">
            <Link
              to="/profile#notifications"
              className="settings-option"
              onClick={() => setShowSettings(false)}
            >
              <Bell size={16} />
              Notification Preferences
            </Link>
            <button 
              onClick={clearHistory} 
              className="settings-option danger"
              disabled={clearingHistory}
            >
              {clearingHistory ? (
                <>
                  <div className="spinner-inline"></div>
                  Clearing...
                </>
              ) : (
                <>
                  <Trash2 size={16} />
                  Clear Chat History
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Chat Area */}
      <div className="chat-main">
        <div className="chat-header">
          <div className="chat-header-left">
            <div className="chat-session-info">
              <h3 className="chat-session-title">{textConfig.appName}</h3>
              <span className="chat-session-meta">
                All conversations
              </span>
            </div>
          </div>
          {/* <div className="chat-header-actions">
            <button 
              className="chat-action-btn" 
              title="Settings"
              onClick={() => setShowSettings(!showSettings)}
            >
              <Settings size={18} />
              <span className="chat-action-label">Settings</span>
            </button>
          </div> */}
        </div>

        <div className={`messages-container${chipsOverlayActive ? ' has-chips' : ''}`}>
          {messages.length === 0 && !loading ? (
            <div className="chat-welcome">
              <h4>Hi {user?.first_name || 'there'}! 👋</h4>
              <p className="welcome-intro">I'm your {textConfig.companyName} {textConfig.appName}</p>
              <p className="welcome-tagline">Your AI community team member</p>

              <ul className="welcome-capabilities">
                <li><span className="welcome-cap-icon" aria-hidden="true">📅</span>Book A Space</li>
                <li><span className="welcome-cap-icon" aria-hidden="true">🛟</span>Report an Issue</li>
                <li><span className="welcome-cap-icon" aria-hidden="true">🔗</span>Meet New People</li>
                <li><span className="welcome-cap-icon" aria-hidden="true">🎉</span>Discover Events</li>
                <li><span className="welcome-cap-icon" aria-hidden="true">📚</span>Ask Questions</li>
                <li><span className="welcome-cap-icon" aria-hidden="true">👋</span>Navigate the Office</li>
              </ul>

              <p className="welcome-cta">Just tell me what you need!</p>
            </div>
          ) : loading && messages.length === 0 ? (
            <ChatMessageSkeleton count={3} />
          ) : (
            <>
              {loadingOlder && (
                <div className="loading-older-messages" role="status" aria-live="polite">
                  Loading older messages…
                </div>
              )}
              {messages.map((message, messageIndex) => (
                <div
                  key={message.id}
                  className={`message ${message.role === 'user' ? 'message-user' : 'message-assistant'}`}
                >
                  {/* Channel Indicator */}
                  {message.channel && (
                    <div className="message-channel">
                      {message.channel === 'sms' ? (
                        <><Smartphone size={14} /> SMS</>
                      ) : (
                        <><Monitor size={14} /> Web</>
                      )}
                    </div>
                  )}
                  <div className="message-icon">
                        {message.role === 'user' ? (
                          (message.user_image || user?.user_image) ? (
                            <img 
                              src={getImageUrl(message.user_image || user?.user_image)} 
                              alt="User"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null
                        ) : (
                          <img 
                            src="/kiln-small-logo.jpg" 
                            alt="AI Assistant"
                            className="assistant-icon-default"
                            onError={(e) => {
                              // If kiln logo fails to load, try custom image
                              if (message.ai_assistant_image || user?.ai_assistant_image) {
                                e.target.src = getImageUrl(message.ai_assistant_image || user?.ai_assistant_image);
                              } else {
                                e.target.style.display = 'none';
                              }
                            }}
                          />
                        )}
                        {message.role === 'user' && (
                          <div className="message-icon-fallback" style={{ 
                            display: (!message.user_image && !user?.user_image) ? 'flex' : 'none' 
                          }}>
                            <User size={20} />
                          </div>
                        )}
                  </div>
                  <div className="message-content">
                    {/* Human sender name (admin reply from Live Conversations). Normal
                        bot responses have no sender_name, so they stay nameless. */}
                    {message.role !== 'user' && (message.sender_name || message.role === 'admin') && (
                      <div className="message-sender-name">{message.sender_name || 'Admin'}</div>
                    )}
                    {/* Room image from extra_metadata - Show for booking confirmations */}
                    {message.extra_metadata?.room_image && message.role === 'assistant' && (
                      <div className="booking-room-image">
                        <img
                          src={message.extra_metadata.room_image}
                          alt={message.extra_metadata.room_name || 'Room'}
                        />
                        {message.extra_metadata.room_name && (
                          <div className="room-name-label">{message.extra_metadata.room_name}</div>
                        )}
                      </div>
                    )}

                    {/* Conditional rendering: Booking card, Room selection, or regular content */}
                    {(() => {
                      // Strip embedded >> [STATE DEBUG] when structured raw_output.debug exists
                      // This prevents double display (structured <pre> block shows it instead)
                      const content = message.raw_output?.debug && message.content
                        ? message.content.replace(/\n*\s*>> \[STATE DEBUG\][\s\S]*$/, '').trim()
                        : message.content;

                      // Support ticket preview / success card (docs/plans/SUPPORT_TICKET_CARD_PLAN.md)
                      const ticketCard = getTicketCard({ ...message, content });
                      if (ticketCard) {
                        const { ticket, image } = findTicketContext(messages, messageIndex);
                        return (
                          <TicketCard
                            card={ticketCard}
                            ticket={ticket}
                            image={image}
                            onImageLoad={() => { if (!userScrolledUpRef.current) anchorLastUserMessageToTop(); }}
                          />
                        );
                      }

                      // Check for booking confirmation card.
                      // update_booking awaiting_confirmation has no `Image:` line in output text — synthesize from raw_output.booking instead.
                      const updateConfirmationData = message.role === 'assistant' ? parseUpdateBookingConfirmation(message) : null;
                      const bookingData = updateConfirmationData || (message.role === 'assistant'
                        ? parseBookingCard(content, { allowMissingImage: isCreateBookingMessage(message) })
                        : null);

                      if (bookingData) {
                        return (
                          <>
                            {/* Persistent header for update_booking confirmations (carries the booking ref) */}
                            {bookingData.headerText && (
                              <p className="update-booking-confirmation-header">{bookingData.headerText}</p>
                            )}
                            {/* Booking Confirmation Success Message - Only show for confirmed bookings, not proposals */}
                            {bookingData.isConfirmed && !bookingData.isProposal && (
                              <div className="booking-confirmation-success">
                                <div className="confirmation-icon">✓</div>
                                <div className="confirmation-text">
                                  <strong>Booking Confirmed!</strong>
                                  {(bookingData.bookingId || bookingData.reference || bookingData.confirmation) && (
                                    <div className="confirmation-reference">
                                      Ref: {bookingData.bookingId || bookingData.reference || bookingData.confirmation}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            <div className="booking-confirmation-card">
                              {bookingData.image && (
                                <div className="booking-card-image">
                                  <img
                                    src={bookingData.image}
                                    alt={bookingData.room}
                                    onLoad={() => { if (!userScrolledUpRef.current) anchorLastUserMessageToTop(); }}
                                  />
                                </div>
                              )}
                              <div className="booking-card-details">
                                <h3 className="booking-room-title">{bookingData.room}</h3>
                                {bookingData.location && (
                                  <div className="booking-detail-row">📍 {bookingData.location}</div>
                                )}
                                {bookingData.date && (
                                  <div className="booking-detail-row">📅 {bookingData.date}</div>
                                )}
                                {/* Time and Duration on one line */}
                                {(bookingData.start || bookingData.end || bookingData.duration || bookingData.timeRange) && (
                                  <div className="booking-detail-row booking-time-highlight">
                                    {bookingData.start && bookingData.end ? (
                                      <>🕐 <strong>{bookingData.start} - {bookingData.end}</strong></>
                                    ) : bookingData.timeRange ? (
                                      <>🕐 <strong>{bookingData.timeRange}</strong></>
                                    ) : bookingData.start ? (
                                      <>🕐 <strong>Start: {bookingData.start}</strong></>
                                    ) : bookingData.end ? (
                                      <>🕐 <strong>End: {bookingData.end}</strong></>
                                    ) : null}
                                    {bookingData.duration && (bookingData.start || bookingData.end || bookingData.timeRange) && (
                                      <span className="duration-inline"> • {bookingData.duration}</span>
                                    )}
                                    {bookingData.duration && !bookingData.start && !bookingData.end && !bookingData.timeRange && (
                                      <>⏱️ <strong>Duration: {bookingData.duration}</strong></>
                                    )}
                                  </div>
                                )}
                                {bookingData.capacity && (
                                  <div className="booking-detail-row">👥 {bookingData.capacity}</div>
                                )}
                                {bookingData.amenities && bookingData.amenities.length > 0 && (
                                  <div className="booking-amenities-row">
                                    <div className="booking-detail-row">🛋️ Amenities</div>
                                    <div className="booking-amenities-list">
                                      {bookingData.amenities.map((amenity) => (
                                        <span key={amenity} className="booking-amenity-tag">{amenity}</span>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Billing Details Section */}
                                {(bookingData.creditsUsed || bookingData.coinsUsed || bookingData.price || bookingData.fee) && (
                                  <div className="booking-billing-section">
                                    <div className="billing-divider">Billing Details</div>

                                    {bookingData.creditsUsed && (
                                      <div className="booking-detail-row billing-item">
                                        <span className="billing-label">Credits Used:</span>
                                        <span className="billing-value">{bookingData.creditsUsed}</span>
                                      </div>
                                    )}
                                    {bookingData.coinsUsed && (
                                      <div className="booking-detail-row billing-item">
                                        <span className="billing-label">Coins Used:</span>
                                        <span className="billing-value">{bookingData.coinsUsed}</span>
                                      </div>
                                    )}
                                    {bookingData.fee && (
                                      <div className="booking-detail-row billing-item">
                                        <span className="billing-label">Fee:</span>
                                        <span className="billing-value">{bookingData.fee}</span>
                                      </div>
                                    )}
                                    {bookingData.price && (
                                      <div className="booking-detail-row billing-item">
                                        <span className="billing-label">Price:</span>
                                        <span className="billing-value">{bookingData.price}</span>
                                      </div>
                                    )}
                                    {bookingData.discountAmount && (
                                      <div className="booking-detail-row billing-item">
                                        <span className="billing-label">Discount Amount:</span>
                                        <span className="billing-value">{bookingData.discountAmount}</span>
                                      </div>
                                    )}
                                    {bookingData.calculatedDiscount && (
                                      <div className="booking-detail-row billing-item">
                                        <span className="billing-label">Calculated Discount:</span>
                                        <span className="billing-value">{bookingData.calculatedDiscount}</span>
                                      </div>
                                    )}
                                    {bookingData.discountedPrice && (
                                      <div className="booking-detail-row billing-item billing-highlight">
                                        <span className="billing-label">Discounted Price:</span>
                                        <span className="billing-value">{bookingData.discountedPrice}</span>
                                      </div>
                                    )}
                                    {bookingData.paymentStatus && (
                                      <div className="booking-detail-row billing-item">
                                        <span className="billing-label">Payment Status:</span>
                                        <span className="billing-value">{bookingData.paymentStatus}</span>
                                      </div>
                                    )}
                                    {bookingData.rate && (
                                      <div className="booking-detail-row billing-item">
                                        <span className="billing-label">Rate:</span>
                                        <span className="billing-value">{bookingData.rate}</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            {bookingData.actionText && (
                              <div className="booking-action-text">
                                {bookingData.actionText.split('\n').map((line, i) => (
                                  <p key={i}>{line}</p>
                                ))}
                              </div>
                            )}
                          </>
                        );
                      }

                      // Room selection
                      if (message.rooms && message.rooms.length > 0) {
                        return (
                          <div className="room-selection-header">
                            {renderMessageContent(
                              parseRoomSelectionHeader(content),
                              message.id,
                              message.role === 'assistant'
                            )}
                          </div>
                        );
                      }

                      // Organization events — show only header text if we have card data
                      // If no event data available, fall through to regular content
                      if (isOrganizationEventMessage(message) && getEventData(message)) {
                        return (
                          <div className="event-cards-header">
                            {renderMessageContent(
                              getEventHeader(content),
                              message.id,
                              message.role === 'assistant'
                            )}
                          </div>
                        );
                      }

                      // Calendar events — hide text entirely, cards handle everything
                      if (isCalendarEventMessage(message) && getCalendarEventData(message)) {
                        return null;
                      }

                      // Booking list — show only viewHeader from raw_output, cards handle the rest
                      if (isBookingListMessage(message) && getBookingListData(message)) {
                        const viewHeader = getBookingListData(message).viewHeader;
                        return viewHeader
                          ? <div className="booking-list-header">{renderMessageContent(viewHeader, message.id, message.role === 'assistant')}</div>
                          : null;
                      }

                      // Cancel booking — show only cancelHeader from raw_output, cards handle the rest
                      if (isCancelBookingMessage(message) && getCancelBookingData(message)) {
                        const cancelHeader = getCancelBookingData(message).cancelHeader;
                        return cancelHeader
                          ? <div className="cancel-booking-header">{renderMessageContent(cancelHeader, message.id, message.role === 'assistant')}</div>
                          : null;
                      }

                      // FAQ answer with files: render per block once the typewriter is done
                      // (the typewriter types the plain `content`; the blocks show the same text).
                      if (hasFaqSegments(message) && typingMessageId !== message.id) {
                        return renderFaqSegments(message);
                      }

                      // Regular content
                      return renderMessageContent(content, message.id, message.role === 'assistant');
                    })()}

                    {/* Files on this message (support ticket photos/videos, FAQ attachments).
                        FAQ replies with faq_segments render their files per block above. */}
                    {!hasFaqSegments(message) && renderAttachmentBlock(message.attachments, message.id)}

                    {message.role === 'assistant' && renderTypingIndicator(message.id)}

                    {/* Web: Room cards - Only show AFTER typewriter finishes */}
                    {message.rooms && message.rooms.length > 0 && typingMessageId !== message.id && (
                      renderRoomSelection(message.rooms)
                    )}

                    {/* Card components (events, calendar, bookings, cancel) - Only show AFTER typewriter finishes */}
                    <MessageCardRenderer
                      message={message}
                      typingMessageId={typingMessageId}
                      collapseSignal={latestUserMessageId}
                      onImageLoad={() => { if (!userScrolledUpRef.current) anchorLastUserMessageToTop(); }}
                      onImageClick={openLightbox}
                      onBookRoom={handleBookCalendarRoom}
                      onCancelSelect={(resourceName) => {
                        setNewMessage(resourceName);
                        setTimeout(() => {
                          if (textareaRef.current) {
                            const syntheticEvent = new Event('submit', { bubbles: true, cancelable: true });
                            textareaRef.current.form.dispatchEvent(syntheticEvent);
                          }
                        }, 100);
                      }}
                      onUpdateSelect={(resourceName) => {
                        setNewMessage(resourceName);
                        setTimeout(() => {
                          if (textareaRef.current) {
                            const syntheticEvent = new Event('submit', { bubbles: true, cancelable: true });
                            textareaRef.current.form.dispatchEvent(syntheticEvent);
                          }
                        }, 100);
                      }}
                    />

                    {/* Debug info - show structured raw_output.debug (text version stripped to avoid double display) */}
                    {message.raw_output?.debug && typingMessageId !== message.id && (
                      <pre className="message-debug-info">
                        {Object.entries(message.raw_output.debug).map(([key, value]) => (
                          <div key={key}>{key}: {typeof value === 'object' ? JSON.stringify(value) : String(value)}</div>
                        ))}
                      </pre>
                    )}
                  </div>

                  {/* Feedback buttons - For assistant and admin reply messages */}
                  {(message.role === 'assistant' || message.role === 'admin') && typingMessageId !== message.id && (
                    <div className="message-feedback">
                      {messageFeedback[message.id]?.submitted ? (
                        // Already submitted - show confirmation
                        <div className="feedback-submitted">
                          {messageFeedback[message.id]?.rating === 'thumbs_up' ? (
                            <><ThumbsUp size={16} className="feedback-icon-active" /> Thank you!</>
                          ) : (
                            <><ThumbsDown size={16} className="feedback-icon-active" /> Thank you for your feedback!</>
                          )}
                        </div>
                      ) : messageFeedback[message.id]?.showTextBox ? (
                        // Thumbs down - show text box
                        <div className="feedback-text-box">
                          <textarea
                            placeholder="What went wrong? (optional)"
                            value={messageFeedback[message.id]?.feedbackText || ''}
                            onChange={(e) => setMessageFeedback(prev => ({
                              ...prev,
                              [message.id]: { ...prev[message.id], feedbackText: e.target.value }
                            }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                submitThumbsDown(message.id);
                              }
                            }}
                            disabled={messageFeedback[message.id]?.submitting}
                            rows={1}
                          />
                          <div className="feedback-actions">
                            <button
                              onClick={() => submitThumbsDown(message.id)}
                              disabled={messageFeedback[message.id]?.submitting}
                              className="btn-submit-feedback"
                            >
                              {messageFeedback[message.id]?.submitting ? 'Submitting...' : 'Submit'}
                            </button>
                            <button
                              onClick={() => cancelFeedback(message.id)}
                              disabled={messageFeedback[message.id]?.submitting}
                              className="btn-cancel-feedback"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        // Show thumbs up/down buttons
                        <div className="feedback-buttons">
                          <button
                            onClick={() => handleThumbsUp(message.id)}
                            disabled={messageFeedback[message.id]?.submitting}
                            className="btn-feedback"
                            title="This was helpful"
                          >
                            <ThumbsUp size={16} />
                          </button>
                          <button
                            onClick={() => handleThumbsDown(message.id)}
                            disabled={messageFeedback[message.id]?.submitting}
                            className="btn-feedback"
                            title="This was not helpful"
                          >
                            <ThumbsDown size={16} />
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="message-time">
                    {message.created_at ? format(new Date(message.created_at), 'hh:mm a') : ''}
                  </div>
                </div>
              ))}
              {sendingMessage && (
                <div className="message message-assistant">
                  <div className="message-icon">
                    <img 
                      src="/kiln-small-logo.jpg" 
                      alt="AI Assistant"
                      className="assistant-icon-default"
                      onError={(e) => {
                        // If kiln logo fails to load, try custom image
                        if (user?.ai_assistant_image) {
                          e.target.src = getImageUrl(user.ai_assistant_image);
                        } else {
                          e.target.style.display = 'none';
                        }
                      }}
                    />
                  </div>
                  <div className="typing-indicator">
                    <div className="typing-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <span className="typing-text">composing a response...</span>
                  </div>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={sendMessage} className="message-input-form">
          {/* Thumbnails ABOVE the input container (ChatGPT style) */}
          {isUploadUIVisible && ((attachments && attachments.length > 0) || uploading) && (
            <div className="attachment-thumbnails-above">
              <FilePreview
                attachments={attachments}
                onRemove={removeAttachment}
                uploading={uploading}
                uploadProgress={uploadProgress}
              />
            </div>
          )}

          {/* Quick-reply chips from latest assistant response; brand-new
              users (no messages yet) get the starter chips instead */}
          <WebChips
            chips={!loading && messages.length === 0 && webChips.length === 0 ? STARTER_CHIPS : webChips}
            onSelect={handleChipSelect}
            disabled={isWaitingForResponse || verificationGated}
          />

          {/* Input container with + icon, textarea, and send button.
              Onboarding gate (A/B): no separate banner — the placeholder
              explains the lock, and clicking the locked composer reopens
              the verify modal (the disabled textarea can't catch clicks,
              so the container handles them). */}
          <div
            className="message-input-container"
            onClick={verificationGated ? () => setShowVerifyModal(true) : undefined}
            style={verificationGated ? { cursor: 'pointer' } : undefined}
          >
            <div className="input-row-with-attachments">
              {/* + icon button (only when creating ticket) */}
              {isUploadUIVisible && (
                <FileUploadButton
                  onFileSelect={handleFileUpload}
                  disabled={
                    uploading ||
                    (attachments && attachments.length >= uploadMaxFiles)
                  }
                />
              )}

              {/* Textarea */}
              <textarea
                ref={textareaRef}
                value={newMessage}
                onChange={(e) => {
                  setNewMessage(e.target.value);
                  adjustTextareaHeight();
                }}
                onKeyDown={handleKeyDown}
                placeholder={verificationGated ? 'Verify your phone number to continue...' : 'Ask a follow-up...'}
                className="message-input"
                rows={1}
                disabled={verificationGated}
                style={verificationGated ? { pointerEvents: 'none' } : undefined}
              />

              {/* Send button */}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!newMessage.trim() || isWaitingForResponse || verificationGated}
                title={verificationGated ? 'Verify your phone number to continue' : isWaitingForResponse ? "Waiting for response..." : "Send message"}
                style={verificationGated ? { pointerEvents: 'none' } : undefined}
              >
                <ArrowUp size={20} />
              </button>
            </div>
          </div>
        </form>
      </div>
      <ImageLightbox
        open={lightbox.open}
        images={lightbox.images}
        startIndex={lightbox.index}
        onClose={closeLightbox}
      />
      {/* Onboarding gate (A/B): in-chat phone verification. Same modal as the
          login-time flow — OTP send/verify/cooldown/escalation all unchanged. */}
      <ProfileCompletionModal
        isOpen={showVerifyModal}
        onClose={() => setShowVerifyModal(false)}
        user={user}
        onSave={handleVerificationComplete}
        gateNotice={GATE_NOTICE}
      />
    </div>
  );
};

export default Chat;